/**
 * SPECTREIQ API Layer
 *
 * WHY: This barrel export provides a clean public API for the frontend.
 * Import from '@/api' rather than specific module files.
 *
 * @example
 * import { api, ApiError, setGlobalMissionContext } from '@/api'
 *
 * // Set mission context (typically from Zustand store)
 * setGlobalMissionContext(missionId)
 *
 * // Make API calls
 * const findings = await api.softwareiq.findings.list(missionId)
 */

// Core utilities
export {
  ApiError,
  setGlobalMissionContext,
  setGlobalAuthToken,
  setGlobalTenantCode,
  request,
  http,
} from './core/client'

// Type exports
export type {
  // Platform types
  Mission,
  CreateMissionPayload,
  // SoftwareIQ types
  Finding,
  CreateFindingPayload,
  Observation,
  ObservationListParams,
  VetObservationPayload,
  VetResult,
  VexDocument,
  SbomDocument,
  ConfidenceLevel,
  ExploitMaturity,
  ObservationStatus,
  VettingDecision,
  JfacRiskScore,
  // Scanner types (Phase 7)
  Scanner,
  ScannerType,
  ScannerStatus,
  ScanJob,
  ToolDefinition,
  ToolRun,
  SourceDefinition,
  SourceCreatePayload,
  SourceUpdatePayload,
  MissionProfile,
  MissionProfilePayload,
  MissionIntegration,
  MissionIntegrationPayload,
  WorkflowStepStatus,
  // Statistics types (Phase 7 - TD-034 resolution)
  RiskTrendPoint,
  MissionStats,
  SeverityCounts,
  // Reporting types (Phase 11)
  ReportExportRequest,
  ReportCapabilities,
  // Bulk vetting types (Phase 12)
  BulkVetRequest,
  BulkVetResult,
  ObservationCounts,
  StigCatalog,
  StigSelection,
  StigSelectionInput,
  StigSyncStatus,
  StigScanRequest,
  // MetadataIQ types
  Schema,
  SchemaField,
  SchemaFieldType,
  SchemaFieldValidation,
  SchemaFieldUiHints,
  SelectOption,
  FieldCondition,
  CreateSchemaPayload,
  Methodology,
  MethodologyLevel,
  IntegrationDefinition,
  IntegrationValidationResponse,
  ProcessDefinition,
  ProcessDefinitionSummary,
  // Assistant types
  AssistantAgentDefinition,
  AssistantAgentsResponse,
  AssistantModelsResponse,
  AssistantProviderCard,
  AssistantModelCard,
  AssistantSession,
  AssistantMessage,
  AssistantMessagesResponse,
  AssistantAttachment,
  AssistantAction,
  // Common types
  PaginationParams,
  FindingListParams,
} from './core/types'

// Module APIs
import { missionsApi, platformApi } from './modules/platform'
import {
  findingsApi,
  observationsApi,
  vexApi,
  sbomApi,
  scannersApi,
  toolsApi,
  toolRunsApi,
  statsApi,
  reportsApi,
  sourcesApi,
  stigsApi,
  missionProfilesApi,
  missionIntegrationsApi,
  workflowStepsApi,
} from './modules/softwareiq'
import { schemasApi, methodologiesApi, modulesApi, processesApi, integrationsApi } from './modules/metadata'
import { controlsApi, assessmentsApi, evidenceApi, auditsApi, auditFindingsApi, complyiqStatsApi, sspRedactionsApi } from './modules/complyiq'
import { assistantApi } from './modules/assistant'

/**
 * Unified API object organized by module.
 * WHY: This structure maintains module isolation in the frontend
 * while providing a convenient namespace for imports.
 *
 * @example
 * import { api } from '@/api'
 *
 * // Platform operations
 * await api.platform.missions.create({ mission_name: 'Test' })
 *
 * // SoftwareIQ operations
 * await api.softwareiq.observations.vet(id, { status: 'confirmed' })
 *
 * // MetadataIQ operations
 * const schema = await api.metadata.schemas.get('softwareiq-finding')
 */
export const api = {
  platform: {
    missions: missionsApi,
    health: platformApi,
  },
  assistant: assistantApi,
  softwareiq: {
    findings: findingsApi,
    observations: observationsApi,
    vex: vexApi,
    sbom: sbomApi,
    scanners: scannersApi,
    tools: toolsApi,
    toolRuns: toolRunsApi,
    stats: statsApi,
    reports: reportsApi,
    sources: sourcesApi,
    stigs: stigsApi,
    missionProfiles: missionProfilesApi,
    missionIntegrations: missionIntegrationsApi,
    workflowSteps: workflowStepsApi,
  },
  complyiq: {
    controls: controlsApi,
    assessments: assessmentsApi,
    evidence: evidenceApi,
    audits: auditsApi,
    auditFindings: auditFindingsApi,
    stats: complyiqStatsApi,
    sspRedactions: sspRedactionsApi,
  },
  metadata: {
    schemas: schemasApi,
    methodologies: methodologiesApi,
    modules: modulesApi,
    processes: processesApi,
    integrations: integrationsApi,
  },
} as const

// Re-export ComplyIQ types
export type {
  Control,
  CreateControlPayload,
  ControlStatus,
  Assessment,
  CreateAssessmentPayload,
  AssessmentStatus,
  Evidence,
  CreateEvidencePayload,
  EvidenceType,
  Audit,
  CreateAuditPayload,
  AuditStatus,
  AuditFinding,
  CreateAuditFindingPayload,
  FindingSeverity as ComplyIQFindingSeverity,
  FindingStatus as ComplyIQFindingStatus,
  ComplyIQStats,
  SspRedactionCategory,
  SspRedactionJob,
  SspRedactionLlmCheck,
  SspRedactionLlmConnection,
  SspRedactionPreview,
  SspRedactionPreviewPage,
  SspRedactionRect,
} from './modules/complyiq'
